import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Flame, ShieldAlert, MessageSquareReply, Activity, AudioLines, HeartHandshake,
  Share2, Sparkles, ArrowRight, Brain, Loader2, TrendingUp, Zap,
} from "lucide-react";
import { Reveal, GlowOrb, SectionHeading, CtaButton } from "./primitives";
import SocialProof from "@/components/SocialProof";

const SectionWrap = ({ children, className = "" }) => (
  <section className={`relative py-20 sm:py-28 px-5 sm:px-6 ${className}`}>
    <div className="max-w-6xl mx-auto relative">{children}</div>
  </section>
);

/* ---------------- Social proof strip ---------------- */
export function SocialProofStrip() {
  return (
    <div className="border-y border-white/5 bg-white/[0.02] py-6 px-5">
      <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-zinc-400">
        <span className="inline-flex items-center gap-2 font-semibold text-white"><Flame size={15} className="text-pink-400" /> Viral sur TikTok</span>
        <SocialProof />
        <span className="inline-flex items-center gap-2">⭐️⭐️⭐️⭐️⭐️ <span className="text-zinc-500">noté par la commu</span></span>
        <span className="inline-flex items-center gap-2 text-zinc-500">WhatsApp · Tinder · Hinge · Insta</span>
      </div>
    </div>
  );
}

/* ---------------- Free quiz hook (interactive teaser) ---------------- */
export function QuizHook() {
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const choose = (c) => {
    setPicked(c); setLoading(true);
    setTimeout(() => { setLoading(false); setDone(true); }, 1100);
  };

  return (
    <SectionWrap>
      <GlowOrb className="w-[420px] h-[420px] -top-20 left-1/4" color="#8b5cf6" opacity={0.16} />
      <div className="grid md:grid-cols-2 gap-10 items-center relative">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold mb-3">Test gratuit · 2 min · sans inscription</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight">
            C'est quoi ton <span className="text-pink-400">style d'attachement</span> ?
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Anxieux, évitant, sécure ou désorganisé ? Découvre comment tu (ou ton/ta crush) fonctionne en amour — et le combo qui vous piège. Résultat partageable en story. 🔥
          </p>
          <Link
            to="/quiz"
            data-testid="engage-quiz-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold py-4 px-8 hover:opacity-90 active:scale-95 transition-transform shadow-[0_0_28px_rgba(236,72,153,0.4)]"
          >
            🧠 Faire le test <ArrowRight size={18} />
          </Link>
        </Reveal>

        <Reveal delay={0.1}>
          <motion.div
            whileHover={{ y: -4 }}
            className="rounded-3xl backdrop-blur-xl bg-zinc-900/60 border border-white/10 p-6 shadow-[0_0_40px_rgba(139,92,246,0.15)]"
            data-testid="quiz-teaser-card"
          >
            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-4">
              <Brain size={14} className="text-purple-400" /> Question 1 / 10
            </div>
            <p className="font-display text-lg font-black text-white leading-snug">
              Il/elle met 5h à répondre à ton message. Ta réaction ?
            </p>

            {!done ? (
              <div className="mt-5 space-y-2.5">
                {[
                  { k: "panic", t: "😰 Je panique et je relance direct" },
                  { k: "chill", t: "😎 Je vis ma vie, ça répondra" },
                ].map((o) => (
                  <button
                    key={o.k}
                    data-testid={`quiz-teaser-${o.k}`}
                    disabled={loading}
                    onClick={() => choose(o.k)}
                    className={`w-full text-left rounded-2xl px-4 py-3.5 text-sm font-medium border transition-colors ${
                      picked === o.k ? "border-pink-500/60 bg-pink-500/10 text-white" : "border-white/10 bg-white/5 text-zinc-300 hover:border-pink-500/40"
                    }`}
                  >
                    {o.t}
                  </button>
                ))}
                {loading && (
                  <p className="flex items-center gap-2 text-sm text-purple-300 pt-1"><Loader2 size={15} className="animate-spin" /> Le Doc analyse…</p>
                )}
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 text-center">
                <p className="text-sm text-zinc-400">Intéressant 👀 Il te reste 9 questions pour révéler ton profil complet.</p>
                <Link
                  to="/quiz"
                  data-testid="quiz-teaser-continue"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-pink-500 text-white text-sm font-bold py-3 px-6 hover:bg-pink-600 active:scale-95 transition-transform"
                >
                  Voir mon résultat <ArrowRight size={16} />
                </Link>
              </motion.div>
            )}
          </motion.div>
        </Reveal>
      </div>
    </SectionWrap>
  );
}

/* ---------------- Features bento grid ---------------- */
const bentoBase = "relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 flex flex-col";

function BentoCard({ className, children, glow, testid }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className={`${bentoBase} ${className}`}
      data-testid={testid}
    >
      {glow && <div className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full blur-[70px]" style={{ background: glow }} />}
      <div className="relative flex-1 flex flex-col">{children}</div>
    </motion.div>
  );
}

export function FeaturesBento() {
  return (
    <SectionWrap>
      <Reveal className="mb-10">
        <SectionHeading
          center
          eyebrow="La boîte à outils"
          title="Tout pour hacker ton game 💘"
          sub="Colle une conversation, laisse le Doc bosser. Voici ce que tu débloques."
        />
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[minmax(180px,auto)] gap-4 md:gap-5 grid-flow-row-dense">
        {/* Verdict — hero card */}
        <BentoCard testid="bento-verdict" className="md:col-span-2 md:row-span-2" glow="rgba(239,68,68,0.35)">
          <p className="text-[11px] uppercase tracking-[0.2em] text-pink-400 font-bold">Le verdict viral</p>
          <h3 className="font-display text-2xl font-black text-white mt-1">Son archétype, sans filtre</h3>
          <p className="text-sm text-zinc-400 mt-2">💣 Love Bomber, 🎭 PN, 🌀 Gaslighteur, 🍞 Intermittent… ou (miracle) 🌿 sain. Avec % de match et badges.</p>
          <div className="mt-auto pt-6">
            <div className="rounded-2xl bg-zinc-950/70 border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-black text-white">💣 Love Bomber</span>
                <span className="font-display text-xl font-black text-pink-400">78%</span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-zinc-400 mt-3 mb-1">
                <span>🌡️ Thermomètre de toxicité</span><span className="font-bold text-red-400">82°</span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "linear-gradient(90deg,#10b981,#f59e0b 55%,#ef4444)", opacity: 0.3 }}>
                <motion.div initial={{ width: 0 }} whileInView={{ width: "82%" }} viewport={{ once: true }} transition={{ duration: 1, ease: "easeOut" }} className="h-full rounded-full bg-red-500" />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {["💣 Love bombing 78%", "🍞 Intermittence 60%", "🌀 Gaslighting 45%"].map((b) => (
                  <span key={b} className="text-[10px] font-semibold text-pink-200 bg-pink-500/10 border border-pink-500/25 rounded-full px-2 py-0.5">{b}</span>
                ))}
              </div>
            </div>
          </div>
        </BentoCard>

        {/* Tracking */}
        <BentoCard testid="bento-tracking" className="md:col-span-2" glow="rgba(34,197,94,0.3)">
          <div className="flex items-center gap-2"><Activity size={16} className="text-emerald-400" /><h3 className="font-display text-lg font-black text-white">Suivi WhatsApp 24/7</h3></div>
          <p className="text-sm text-zinc-400 mt-1">Ton health score évolue en temps réel. Alerte dès qu'un red flag surgit.</p>
          <div className="mt-auto pt-4 flex items-end gap-1 h-16">
            {[40, 52, 48, 61, 55, 70, 66, 78, 72, 84].map((h, i) => (
              <motion.div key={i} initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex-1 rounded-t bg-gradient-to-t from-emerald-500/30 to-emerald-400" />
            ))}
          </div>
        </BentoCard>

        {/* Rizz */}
        <BentoCard testid="bento-rizz" glow="rgba(236,72,153,0.35)">
          <div className="flex items-center gap-2"><Flame size={16} className="text-pink-400" /><h3 className="font-display text-base font-black text-white">Rizz Score</h3></div>
          <p className="text-xs text-zinc-400 mt-1">Ton score de charme + une réponse qui tue, générée pour toi.</p>
          <div className="mt-auto pt-3">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-pink-500/15 border border-pink-500/30 text-pink-300 text-xs font-bold px-3 py-1"><Zap size={12} /> +99 Rizz</div>
          </div>
        </BentoCard>

        {/* Red flags */}
        <BentoCard testid="bento-redflags" glow="rgba(239,68,68,0.35)">
          <div className="flex items-center gap-2"><ShieldAlert size={16} className="text-red-400" /><h3 className="font-display text-base font-black text-white">Red Flags</h3></div>
          <p className="text-xs text-zinc-400 mt-1">Repérés avec citations preuves, pas au feeling.</p>
          <div className="mt-auto pt-3 space-y-1">
            <div className="h-2 rounded bg-white/10 blur-[1px] w-4/5" />
            <div className="h-2 rounded bg-red-500/60 w-3/5" />
          </div>
        </BentoCard>

        {/* Compatibility */}
        <BentoCard testid="bento-compatibility" className="md:col-span-2" glow="rgba(139,92,246,0.3)">
          <div className="flex items-center gap-2"><HeartHandshake size={16} className="text-purple-400" /><h3 className="font-display text-lg font-black text-white">Compatibilité & combo d'attachement</h3></div>
          <p className="text-sm text-zinc-400 mt-1">« 🥶 Évitant ↔ 😰 Anxieux = le piège classique ». Ton test perso réutilisé dans l'analyse.</p>
          <div className="mt-auto pt-4 flex items-center gap-3 text-sm font-bold">
            <span>😰 Anxieux</span><MessageSquareReply size={16} className="text-zinc-500 rotate-90" /><span>🥶 Évitant</span>
            <span className="ml-auto text-purple-300">65% match</span>
          </div>
        </BentoCard>

        {/* Audio / Docteur */}
        <BentoCard testid="bento-audio" className="md:col-span-2" glow="rgba(236,72,153,0.3)">
          <div className="flex items-center gap-2"><AudioLines size={16} className="text-pink-400" /><h3 className="font-display text-lg font-black text-white">Consultations vocales « Docteur »</h3></div>
          <p className="text-sm text-zinc-400 mt-1">Parle au Doc en vocal + plan d'action multi-semaines pour reprendre le contrôle.</p>
          <div className="mt-auto pt-4 flex items-center gap-1 h-8">
            {[6, 14, 22, 10, 26, 18, 30, 12, 24, 8, 20, 28, 10, 16].map((h, i) => (
              <motion.div key={i} animate={{ height: [`${h}px`, `${h + 8}px`, `${h}px`] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.08 }}
                className="w-1 rounded-full bg-pink-400/70" />
            ))}
          </div>
        </BentoCard>
      </div>
    </SectionWrap>
  );
}

/* ---------------- Creator program band ---------------- */
export function CreatorBand() {
  return (
    <SectionWrap>
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-pink-500/25 bg-gradient-to-br from-pink-500/10 to-purple-500/10 p-8 sm:p-10 text-center">
          <GlowOrb className="w-64 h-64 -top-20 -right-10" color="#EC4899" opacity={0.2} />
          <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold">Créateurs & influenceurs</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">Gagne <span className="text-pink-400">50%</span> en parlant de DocLove 🤝</h2>
          <p className="mt-3 text-zinc-400 max-w-xl mx-auto">TikTokeur·se, UGC, influenceur·se ? 50% le 1ᵉʳ mois + 25% à vie sur chaque abonné·e. Auto-approuvé, zéro plafond.</p>
          <Link to="/createurs" data-testid="home-creators-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-zinc-950 font-bold py-3.5 px-7 hover:bg-zinc-100 active:scale-95 transition-transform">
            Rejoindre le programme <ArrowRight size={18} />
          </Link>
        </div>
      </Reveal>
    </SectionWrap>
  );
}
function MiniRadar() {
  const pts = [90, 40, 75, 85, 30, 60, 50, 70]; // narcissisme, empathie, ...
  const cx = 90, cy = 90, r = 70, n = pts.length;
  const coord = (val, i) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    const rr = (val / 100) * r;
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  };
  const poly = pts.map((v, i) => coord(v, i).join(",")).join(" ");
  const ring = (mult) => Array.from({ length: n }).map((_, i) => coord(100 * mult, i).join(",")).join(" ");
  return (
    <svg viewBox="0 0 180 180" className="w-40 h-40">
      {[0.33, 0.66, 1].map((m) => <polygon key={m} points={ring(m)} fill="none" stroke="rgba(255,255,255,0.08)" />)}
      <motion.polygon initial={{ opacity: 0, scale: 0.6 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
        points={poly} fill="rgba(236,72,153,0.25)" stroke="#EC4899" strokeWidth="2" style={{ transformOrigin: "center" }} />
    </svg>
  );
}

export function VerdictDeepDive() {
  return (
    <SectionWrap>
      <GlowOrb className="w-[380px] h-[380px] top-10 -right-20" color="#EC4899" opacity={0.14} />
      <div className="grid md:grid-cols-2 gap-12 items-center relative">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold mb-3">Profil psychologique</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight">
            Le verdict qui fait mal <span className="text-pink-400">(mais qui te sauve).</span>
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Radar de personnalité sur 8 dimensions, tendances (PN, love bombing, gaslighting…), thermomètre de toxicité et carte partageable. On ne pose pas de diagnostic — on te montre les signaux, preuves à l'appui.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-zinc-300">
            {["Radar 8 dimensions (empathie, emprise, narcissisme…)", "Comparaison Moi ↔ l'autre", "Carte story partageable"].map((t) => (
              <li key={t} className="flex items-start gap-2"><Sparkles size={15} className="text-pink-400 shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
          <div className="mt-6"><CtaButton testid="verdict-deepdive-cta" event="lead">Décoder ma conv <ArrowRight size={18} /></CtaButton></div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative w-full max-w-sm mx-auto rounded-[2rem] bg-zinc-950 border border-pink-500/30 shadow-[0_0_40px_rgba(236,72,153,0.2)] p-7" data-testid="verdict-mock-card">
            <div className="flex items-center gap-2 text-white">
              <Brain size={18} className="text-pink-400" /><span className="font-display font-black tracking-tight">DocLove</span>
              <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">Verdict</span>
            </div>
            <div className="text-center mt-5">
              <div className="text-5xl">🎭</div>
              <p className="font-display text-2xl font-black text-white mt-2">Le Manipulateur</p>
              <p className="text-xs text-zinc-500">tendances pervers narcissique</p>
            </div>
            <div className="flex justify-center my-2"><MiniRadar /></div>
            <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1">
              <span>🌡️ Toxicité</span><span className="font-bold text-red-400">92°</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
              <motion.div initial={{ width: 0 }} whileInView={{ width: "92%" }} viewport={{ once: true }} transition={{ duration: 1 }} className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#f59e0b,#ef4444)" }} />
            </div>
            <button data-testid="verdict-mock-share" className="mt-5 w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-bold py-3">
              <Share2 size={16} /> Partager en story
            </button>
          </div>
        </Reveal>
      </div>
    </SectionWrap>
  );
}
